import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowForward,
  CheckCircleOutline,
  Close,
  DeleteOutline,
  EditOutlined,
  ImageOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CustomSelect from "../../components/common/CustomSelect";
import CustomTextField from "../../components/common/CustomTextField";
import CustomButton from "../../components/common/CustomButton";
import SadhuSadhviVisitService from "../../Service/SadhuSadhviVisitService";
import { getPublicS3Url } from "../../utils/s3PublicUrl";

const defaultValues = {
  state: "",
  district: "",
  taluka: "",
  village: "",
  sadhuName: "",
  contactPerson: "",
  contactNumber: "",
  sthanak: "",
  visitDate: "",
  visitTime: "",
  shortDescription: "",
};

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Unable to submit the visit. Please try again.";

const FilePicker = ({ label, file, onChange, icon, disabled = false }) => {
  const inputRef = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const isRemoteFile = typeof file === "string";
  const previewUrl = useMemo(() => {
    if (!file) return "";
    return isRemoteFile ? getPublicS3Url(file) : URL.createObjectURL(file);
  }, [file, isRemoteFile]);
  const fileLabel = !isRemoteFile && file?.name ? file.name : label;

  useEffect(() => {
    return () => {
      if (previewUrl && !isRemoteFile) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl, isRemoteFile]);

  useEffect(() => {
    window.scrollTo({top:"0", behaviour:"smooth"})
  },[])

  const handleFileChange = (event) => {
    if (disabled) return;
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const allowedTypes = ["image/jpeg", "image/png"];

    if (!allowedTypes.includes(selectedFile.type)) {
      alert("Only JPG and PNG images are allowed");
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      alert("Image must be under 5 MB");
      return;
    }

    onChange(selectedFile);
    event.target.value = "";
  };

  return (
    <Box>
      {file ? (
        <Box
          sx={{
            width: "100%",
            minHeight: 180,
            border: "2px dashed #d0d5dd",
            borderRadius: 1,
            bgcolor: "background.paper",
            overflow: "hidden",
            p: 1,
            position: "relative",
            textAlign: "center",
            "&:hover": {
              borderColor: "primary.main",
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              display: "flex",
              gap: 0.5,
              bgcolor: "rgba(255,255,255,0.92)",
              borderRadius: 1,
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.16)",
            }}
          >
            <IconButton
              type="button"
              size="small"
              color="primary"
              aria-label={`View ${label}`}
              onClick={() => setPreviewOpen(true)}
            >
              <VisibilityOutlined fontSize="small" />
            </IconButton>
            {!disabled && (
              <>
                <IconButton
                  type="button"
                  size="small"
                  color="primary"
                  aria-label={`Replace ${label}`}
                  onClick={() => {
                    if (!disabled) inputRef.current?.click();
                  }}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
                <IconButton
                  type="button"
                  size="small"
                  color="error"
                  aria-label={`Delete ${label}`}
                  onClick={() => {
                    onChange(null);
                    setPreviewOpen(false);
                  }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>

          <Box
            component="img"
            src={previewUrl}
            alt={fileLabel}
            sx={{
              width: "100%",
              height: 164,
              objectFit: "cover",
              borderRadius: 1,
            }}
          />
        </Box>
      ) : (
        <Box
          component="button"
          type="button"
          onClick={() => {
            if (!disabled) inputRef.current?.click();
          }}
          sx={{
            width: "100%",
            minHeight: 180,
            border: "2px dashed #d0d5dd",
            borderRadius: 1,
            bgcolor: "background.paper",
            cursor: disabled ? "default" : "pointer",
            overflow: "hidden",
            p: 1,
            position: "relative",
            "&:hover": {
              borderColor: "primary.main",
            },
          }}
        >
          <Box
            sx={{
              height: 160,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            {icon}

            <Typography>{disabled ? "No image attached" : label}</Typography>

            <Typography variant="caption" color="text.secondary">
              Max 5 MB - JPG, PNG
            </Typography>
          </Box>
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        hidden
        accept="image/jpeg,image/png"
        disabled={disabled}
        onChange={handleFileChange}
      />

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {fileLabel}
          <IconButton
            type="button"
            onClick={() => setPreviewOpen(false)}
            sx={{ position: "absolute", right: 12, top: 12 }}
            aria-label="Close image preview"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            component="img"
            src={previewUrl}
            alt={fileLabel}
            sx={{
              display: "block",
              width: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
              borderRadius: 1,
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};
const FormSection = ({ children }) => (
  <>
    {children}
  </>
);
const SadhuSadhviVisitForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const viewMode = Boolean(id);
  const { control, reset, setValue, getValues, handleSubmit } = useForm({
    defaultValues,
  });
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [villages, setVillages] = useState([]);
  const [visitPhoto1, setVisitPhoto1] = useState(null);
  const [visitPhoto2, setVisitPhoto2] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingVisit, setLoadingVisit] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const stateId = useWatch({ control, name: "state" });
  const districtId = useWatch({ control, name: "district" });
  const talukaId = useWatch({ control, name: "taluka" });

  const stateOptions = useMemo(
    () => states.map((item) => ({ value: item._id, label: item.stateName })),
    [states],
  );
  const districtOptions = useMemo(
    () =>
      districts.map((item) => ({ value: item._id, label: item.districtName })),
    [districts],
  );
  const talukaOptions = useMemo(
    () => talukas.map((item) => ({ value: item._id, label: item.talukaName })),
    [talukas],
  );
  const villageOptions = useMemo(
    () =>
      villages.map((item) => ({ value: item._id, label: item.villageName })),
    [villages],
  );

  const showError = (message) =>
    setNotification({ open: true, message, severity: "error" });

  const getEntityId = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    return value._id || value.id || "";
  };

  useEffect(() => {
    if (!viewMode) return undefined;

    let active = true;
    setLoadingVisit(true);

    SadhuSadhviVisitService.getMyVisitById(id)
      .then((visit) => {
        if (!active || !visit) return;
        reset({
          ...defaultValues,
          state: getEntityId(visit.state),
          district: getEntityId(visit.district),
          taluka: getEntityId(visit.taluka),
          village: getEntityId(visit.village),
          sadhuName: visit.sadhuName || "",
          contactPerson: visit.contactPerson || "",
          contactNumber: visit.contactNumber || "",
          sthanak: visit.sthanak || "",
          visitDate: visit.visitDate || "",
          visitTime: visit.visitTime || "",
          shortDescription: visit.shortDescription || "",
        });
        setVisitPhoto1(visit.visitPhoto1 || null);
        setVisitPhoto2(visit.visitPhoto2 || null);
      })
      .catch((requestError) => {
        if (active) showError(getErrorMessage(requestError));
      })
      .finally(() => {
        if (active) setLoadingVisit(false);
      });

    return () => {
      active = false;
    };
  }, [id, reset, viewMode]);

  useEffect(() => {
    SadhuSadhviVisitService.getStates()
      .then(setStates)
      .catch(() => showError("Unable to load states."));
  }, []);

  useEffect(() => {
    if (!viewMode) {
      setValue("district", "");
      setValue("taluka", "");
      setValue("village", "");
    }
    setDistricts([]);
    setTalukas([]);
    setVillages([]);
    if (stateId) {
      SadhuSadhviVisitService.getDistricts(stateId)
        .then(setDistricts)
        .catch(() => showError("Unable to load districts."));
    }
  }, [stateId, setValue, viewMode]);

  useEffect(() => {
    if (!viewMode) {
      setValue("taluka", "");
      setValue("village", "");
    }
    setTalukas([]);
    setVillages([]);
    if (districtId) {
      SadhuSadhviVisitService.getTalukas(districtId)
        .then(setTalukas)
        .catch(() => showError("Unable to load talukas."));
    }
  }, [districtId, setValue, viewMode]);

  useEffect(() => {
    if (!viewMode) {
      setValue("village", "");
    }
    setVillages([]);
    if (talukaId) {
      SadhuSadhviVisitService.getVillages(talukaId)
        .then(setVillages)
        .catch(() => showError("Unable to load villages."));
    }
  }, [talukaId, setValue, viewMode]);
  const openReview = () => {
    if (viewMode) return;
    if (!visitPhoto1) {
      showError("Visit photo 1 is required.");
      return;
    }
    setReviewOpen(true);
  };

  const submitVisit = async () => {
    setSubmitting(true);
    try {
      await SadhuSadhviVisitService.submitVisit(getValues(), {
        visitPhoto1,
        visitPhoto2,
      });
      setReviewOpen(false);
      setSuccessOpen(true);
      reset(defaultValues);
      setVisitPhoto1(null);
      setVisitPhoto2(null);
      setDistricts([]);
      setTalukas([]);
      setVillages([]);
    } catch (error) {
      showError(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedLabel = (options, value) =>
    options.find((option) => String(option.value) === String(value))?.label ||
    "—";
  const reviewValues = getValues();
  const getVisitRoute = (routeName) => {
    const segments = location.pathname.split("/").filter(Boolean);
    const markerIndex = segments.findIndex(
      (segment) =>
        segment === "sadhuSadhviVisitForm" ||
        segment === "sadhuSadhviVisitForms",
    );
    const prefix =
      markerIndex > 0 ? "/" + segments.slice(0, markerIndex).join("/") : "";

    return prefix + "/" + routeName;
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(openReview)}
        sx={{
          maxWidth: "1200px",
          mx: "auto",
          px: 3,
          pt: { xs: 2, md: 2.5 },
          pb: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box
          sx={{
            position: "relative",
            minHeight: 42,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            rowGap: 1,
          }}
        >
          <Button
            variant="contained"
            onClick={() => navigate(getVisitRoute("sadhuSadhviVisitForms"))}
            sx={{
              position: { xs: "static", sm: "absolute" },
              right: 0,
              alignSelf: { xs: "flex-end", sm: "center" },
            }}
          >
            {viewMode ? "Back to Submitted Forms" : "View Submitted Forms"}
          </Button>

          <Typography
            variant="h3"
            sx={{
              position: { xs: "static", sm: "absolute" },
              left: { sm: "50%" },
              transform: { sm: "translateX(-50%)" },
              width: { xs: "100%", sm: "auto" },
              ml: { xs: 2, sm: 0 },
              textAlign: "center",
              fontWeight: 600,
              whiteSpace: { sm: "nowrap" },
            }}
          >
            {viewMode ? "Sadhu Sadhvi Visit Details" : "Sadhu Sadhvi Visit Form"}
          </Typography>
        </Box>

        {loadingVisit && (
          <Alert severity="info">Loading submitted visit details...</Alert>
        )}

        <FormSection>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mb: 1.5,
            }}
          >
            <CustomTextField
              control={control}
              label="Sthanak"
              name="sthanak"
              disabled={viewMode}
              required
            />

            <CustomTextField
              control={control}
              label="Sadhu/Sadhvi Name"
              name="sadhuName"
              disabled={viewMode}
              required
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "repeat(4, 1fr)",
              },
              gap: 2,
            }}
          >
            <CustomSelect
              control={control}
              label="State"
              name="state"
              options={stateOptions}
              disabled={viewMode}
              required
            />

            <CustomSelect
              control={control}
              label="District"
              name="district"
              options={districtOptions}
              disabled={viewMode || !stateId}
              required
            />

            <CustomSelect
              control={control}
              label="Taluka"
              name="taluka"
              options={talukaOptions}
              disabled={viewMode || !districtId}
              required
            />

            <CustomSelect
              control={control}
              label="Village"
              name="village"
              options={villageOptions}
              disabled={viewMode || !talukaId}
              required
            />
          </Box>
        </FormSection>

        <FormSection>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "repeat(4, 1fr)",
              },
              gap: 2,
            }}
          >
            <CustomTextField
              control={control}
              label="Sanghpati Name"
              name="contactPerson"
              disabled={viewMode}
              required
            />

            <CustomTextField
              control={control}
              label="Sanghpati Mobile Number"
              name="contactNumber"
              disabled={viewMode}
              required
              maxLength={10}
              rules={{
                pattern: {
                  value: /^\d{10}$/,
                  message: "Enter a valid 10 digit mobile number",
                },
              }}
            />

            <CustomTextField
              control={control}
              label="Visit Date"
              name="visitDate"
              disabled={viewMode}
              type="date"
              required
              InputLabelProps={{ shrink: true }}
            />

            <CustomTextField
              control={control}
              label="Visit Time"
              name="visitTime"
              disabled={viewMode}
              type="time"
              required
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </FormSection>

        <FormSection>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "2fr 1fr 1fr",
              },
              gap: 2,
              alignItems: "center",
            }}
          >
            <CustomTextField
              control={control}
              label="Purpose of Visit"
              name="shortDescription"
              disabled={viewMode}
              multiline
              rows={6}
              sx={{
                height: "100%",
                "& .MuiInputBase-root": {
                  height: "100%",
                  alignItems: "flex-start",
                },
                "& textarea": {
                  height: "100% !important",
                },
              }}
            />

            <FilePicker
              label="Upload Visit Photo 1"
              file={visitPhoto1}
              onChange={setVisitPhoto1}
              icon={<ImageOutlined color="primary" />}
              disabled={viewMode}
            />

            <FilePicker
              label="Upload Visit Photo 2 (optional)"
              file={visitPhoto2}
              onChange={setVisitPhoto2}
              icon={<ImageOutlined color="primary" />}
              disabled={viewMode}
            />
          </Box>
        </FormSection>

        {!viewMode && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <CustomButton type="submit">Submit</CustomButton>
          </Box>
        )}

        <Dialog
          open={reviewOpen}
          onClose={submitting ? undefined : () => setReviewOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
            Confirm Visit Submission
            <IconButton
              onClick={() => setReviewOpen(false)}
              disabled={submitting}
              sx={{ position: "absolute", right: 12, top: 12 }}
              aria-label="Close"
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Please review the visit details before submitting.
            </Typography>
            <Stack spacing={1.25}>
              <Typography>
                <strong>Sadhu/Sadhvi:</strong> {reviewValues.sadhuName}
              </Typography>
              <Typography>
                <strong>Contact:</strong> {reviewValues.contactPerson} (
                {reviewValues.contactNumber})
              </Typography>
              <Typography>
                <strong>Location:</strong>{" "}
                {selectedLabel(villageOptions, reviewValues.village)},{" "}
                {selectedLabel(talukaOptions, reviewValues.taluka)},{" "}
                {selectedLabel(districtOptions, reviewValues.district)},{" "}
                {selectedLabel(stateOptions, reviewValues.state)}
              </Typography>
              <Typography>
                <strong>Sthanak:</strong> {reviewValues.sthanak}
              </Typography>
              <Typography>
                <strong>Visit date/time:</strong> {reviewValues.visitDate}{" "}
                {reviewValues.visitTime}
              </Typography>
              <Typography>
                <strong>Visit photo 1:</strong> {visitPhoto1?.name || "Attached"}
              </Typography>
              <Typography>
                <strong>Visit photo 2:</strong>{" "}
                {visitPhoto2?.name || (visitPhoto2 ? "Attached" : "Not attached")}
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setReviewOpen(false)} disabled={submitting}>
              Edit
            </Button>
            <CustomButton
              onClick={submitVisit}
              loading={submitting}
              disabled={submitting}
            >
              Confirm & Submit
            </CustomButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={successOpen}
          onClose={() => setSuccessOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogContent sx={{ textAlign: "center", pt: 4 }}>
            <CheckCircleOutline
              sx={{ fontSize: 64, color: "success.main", mb: 1 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Visit submitted successfully
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              The Sadhu/Sadhvi visit details have been saved.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
            <CustomButton onClick={() => setSuccessOpen(false)}>
              Done
            </CustomButton>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={notification.open}
          autoHideDuration={5000}
          onClose={() =>
            setNotification((current) => ({ ...current, open: false }))
          }
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity={notification.severity}
            onClose={() =>
              setNotification((current) => ({ ...current, open: false }))
            }
            variant="filled"
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default SadhuSadhviVisitForm;
